import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Quote, Heading2, Heading3,
  Link as LinkIcon, Eraser,
  AlignLeft, AlignCenter, AlignRight, IndentIncrease, IndentDecrease,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { sanitizeCorpusHtml } from "@/components/richTextEditorUtils";
// ImageCaptionNode removed in the Insert Reference cleanup.
import { BriefEditorContext } from "../draft/briefEditorContext";
import { supabase } from "@/integrations/supabase/client";
import {
  uploadWorkspaceMedia,
  WorkspaceMediaCapError,
  WORKSPACE_MEDIA_ALLOWED_TYPES,
} from "@/lib/workspaceMedia";
import { useWorkspace } from "@/hooks/useWorkspace";
import { tierEntitlements } from "@/lib/tier";
import { toast } from "sonner";
import { notifyHostDropHandled } from "@/lib/composerHostDropReset";
import { WordPasteSanitizer } from "@/lib/editor/wordPasteSanitizer";
import { BlockIndent } from "@/lib/editor/indentExtension";
import { CorpusTopicPicker } from "./CorpusTopicPicker";

type Props = {
  initialTitle: string;
  initialBody: string;
  initialTopicIds?: string[];
  /** When true, the Topics row is rendered above the body. */
  codexEnabled?: boolean;
  busy?: boolean;
  workspaceId: string | null;
  onSave: (title: string, body: string, topicIds: string[]) => void | Promise<void>;
  onCancel: () => void;
};

// Wrap legacy plain-text bodies (no HTML tags) into paragraphs so TipTap
// renders them as structured content rather than a single blob.
const normalizeIncomingBody = (body: string): string => {
  if (!body) return "";
  if (/<[a-z][\s\S]*>/i.test(body)) return body;
  return body
    .split(/\r?\n/)
    .map((line) => (line.trim() === "" ? "<p></p>" : `<p>${escapeHtml(line)}</p>`))
    .join("");
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const CorpusDocumentEditor = ({
  initialTitle,
  initialBody,
  initialTopicIds,
  codexEnabled,
  busy,
  workspaceId,
  onSave,
  onCancel,
}: Props) => {
  const [title, setTitle] = useState(initialTitle);
  const [topicIds, setTopicIds] = useState<string[]>(initialTopicIds ?? []);

  // Resolve tier cap for Workspace Media uploads triggered by paste/drop.
  const { workspaces } = useWorkspace();
  const workspaceMediaCapBytes = useMemo(() => {
    if (!workspaceId) return null;
    const ws = workspaces.find((w) => w.id === workspaceId);
    return tierEntitlements(ws?.account_tier ?? null).workspaceMediaBytes;
  }, [workspaceId, workspaces]);
  const capRef = useRef<number | null>(workspaceMediaCapBytes);
  useEffect(() => { capRef.current = workspaceMediaCapBytes; }, [workspaceMediaCapBytes]);

  const editorRef = useRef<import("@tiptap/react").Editor | null>(null);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        horizontalRule: {},
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Placeholder.configure({ placeholder: "Paste or type the source content here…" }),
      Underline,
      TextAlign.configure({ types: ["paragraph", "heading"] }),
      BlockIndent,
      WordPasteSanitizer,
    ],
    content: normalizeIncomingBody(initialBody),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm dark:prose-invert max-w-none min-h-full px-3 py-2 focus:outline-none text-sm leading-relaxed",
      },
    },
  });

  useEffect(() => { editorRef.current = editor; }, [editor]);

  // Reload content when switching between documents.
  useEffect(() => {
    if (!editor) return;
    const incoming = normalizeIncomingBody(initialBody);
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, false);
    }
  }, [initialBody, editor]);


  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);

  useEffect(() => {
    setTopicIds(initialTopicIds ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTopicIds?.join(",")]);

  const handleTitlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (title.trim().length > 0) return;
    const text = e.clipboardData.getData("text");
    if (!text) return;
    const firstLine = text.split(/\r?\n/).find((l) => l.trim().length > 0)?.trim() ?? "";
    if (firstLine) {
      e.preventDefault();
      setTitle(firstLine.slice(0, 120));
    }
  };

  const handleSetLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  // Image insert was removed with the ImageCaption node cleanup.

  const handleSave = () => {
    if (!editor) return;
    const html = sanitizeCorpusHtml(editor.getHTML());
    const isEmpty = editor.getText().trim().length === 0 && !/data-media-id=/.test(html);
    onSave(title, isEmpty ? "" : html, topicIds);
  };

  const ctxValue = useMemo(
    () => ({
      workspaceId: workspaceId ?? null,
      briefId: null,
      references: [],
      workspaceMediaCapBytes,
    }),
    [workspaceId, workspaceMediaCapBytes],
  );

  return (
    <BriefEditorContext.Provider value={ctxValue}>
      <div className="flex h-full flex-col gap-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onPaste={handleTitlePaste}
          placeholder="Document title"
          className="h-8"
        />
        {codexEnabled && workspaceId && (
          <CorpusTopicPicker
            workspaceId={workspaceId}
            value={topicIds}
            onChange={setTopicIds}
            compact
          />
        )}
        {editor && (
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-input bg-background p-1">
            <Toggle size="sm" pressed={editor.isActive("heading", { level: 2 })}
              onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              aria-label="Heading 2">
              <Heading2 className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("heading", { level: 3 })}
              onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              aria-label="Heading 3">
              <Heading3 className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("bold")}
              onPressedChange={() => editor.chain().focus().toggleBold().run()} aria-label="Bold">
              <Bold className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("italic")}
              onPressedChange={() => editor.chain().focus().toggleItalic().run()} aria-label="Italic">
              <Italic className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("underline")}
              onPressedChange={() => editor.chain().focus().toggleUnderline().run()} aria-label="Underline">
              <UnderlineIcon className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("bulletList")}
              onPressedChange={() => editor.chain().focus().toggleBulletList().run()} aria-label="Bullet list">
              <List className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("orderedList")}
              onPressedChange={() => editor.chain().focus().toggleOrderedList().run()} aria-label="Numbered list">
              <ListOrdered className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("blockquote")}
              onPressedChange={() => editor.chain().focus().toggleBlockquote().run()} aria-label="Blockquote">
              <Quote className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive("link")}
              onPressedChange={handleSetLink} aria-label="Link">
              <LinkIcon className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive({ textAlign: "left" })}
              onPressedChange={() => editor.chain().focus().setTextAlign("left").run()} aria-label="Align left">
              <AlignLeft className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive({ textAlign: "center" })}
              onPressedChange={() => editor.chain().focus().setTextAlign("center").run()} aria-label="Align center">
              <AlignCenter className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive({ textAlign: "right" })}
              onPressedChange={() => editor.chain().focus().setTextAlign("right").run()} aria-label="Align right">
              <AlignRight className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={false}
              onPressedChange={() => {
                const ok = editor.isActive("listItem")
                  ? editor.chain().focus().liftListItem("listItem").run()
                  : false;
                if (!ok) editor.chain().focus().outdentBlock().run();
              }}
              aria-label="Decrease indent">
              <IndentDecrease className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={false}
              onPressedChange={() => {
                const ok = editor.isActive("listItem")
                  ? editor.chain().focus().sinkListItem("listItem").run()
                  : false;
                if (!ok) editor.chain().focus().indentBlock().run();
              }}
              aria-label="Increase indent">
              <IndentIncrease className="h-4 w-4" />
            </Toggle>
            <Toggle size="sm" pressed={false}
              onPressedChange={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
              aria-label="Clear formatting">
              <Eraser className="h-4 w-4" />
            </Toggle>
          </div>
        )}
        <div className="flex-1 overflow-auto rounded-md border border-input bg-background">
          <EditorContent editor={editor} className="h-full" />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </BriefEditorContext.Provider>
  );
};
